import React, { useContext, useEffect, useState } from 'react'
import {
  Alert,
  Avatar,
  Box,
  Button,
  Container,
  CssBaseline,
  Fade,
  Link,
  TextField,
  Typography,
} from '@mui/material'
import { LockOutlined } from '@mui/icons-material'
import { Helmet } from 'react-helmet'
import { useNavigate } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import Context from '../../../contexts/Context'
import { getTenantToken, removeTenantToken } from '../../../services/Tenant/TenantToken'
import { removeTenantSession } from '../../../services/Tenant/TenantStorage'
import { apiTenant } from '../../../services/Tenant/ApiTenant'
import Masks from '../../../components/Shared/Masks'
import OtpInput from '../../../components/Tenant/OtpInput'
import LoadingButton from '@mui/lab/LoadingButton'
import CircularProgress from '@mui/material/CircularProgress'

const TenantLogin = () => {
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const { signInTenant, validateTenantOtp } = useContext(Context)

  const [step, setStep] = useState('login') // 'login' or 'otp'
  const [cpf, setCpf] = useState('')
  const [email, setEmail] = useState('')
  const [otpCode, setOtpCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [timer, setTimer] = useState(0)

  // Clear residual auth on mount
  useEffect(() => {
    removeTenantToken()
    removeTenantSession()
  }, [])

  // Redirect if already authenticated
  useEffect(() => {
    if (getTenantToken()) {
      navigate('/area-cliente/', { replace: true })
    }
  }, [])

  // Countdown timer for OTP
  useEffect(() => {
    if (timer <= 0) return
    const interval = setInterval(() => {
      setTimer(prev => prev - 1)
    }, 1000)
    return () => clearInterval(interval)
  }, [timer])

  const formatTimer = (seconds) => {
    const m = Math.floor(seconds / 60)
    const s = seconds % 60
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
  }

  const handleRequestOtp = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      await signInTenant({ cpf, email })
      setStep('otp')
      setTimer(600) // 10 minutes
      setOtpCode('')
      enqueueSnackbar('Se os dados estiverem corretos, um código será enviado para o email informado.', { variant: 'info' })
    } catch (err) {
      setError('Erro ao solicitar código. Tente novamente.')
    } finally {
      setLoading(false)
    }
  }

  const handleValidateOtp = async (e) => {
    e.preventDefault()
    if (otpCode.length !== 6) {
      setError('Digite o código de 6 dígitos.')
      return
    }
    setError('')
    setLoading(true)

    try {
      await validateTenantOtp({ cpf, email, code: otpCode })
    } catch (err) {
      const errorMsg = err?.response?.data?.error || 'Erro ao validar código. Tente novamente.'
      setError(errorMsg)
      setOtpCode('')
    } finally {
      setLoading(false)
    }
  }

  const handleResendCode = async () => {
    setError('')
    setLoading(true)
    try {
      await signInTenant({ cpf, email })
      setTimer(600)
      setOtpCode('')
      enqueueSnackbar('Novo código enviado.', { variant: 'info' })
    } catch (err) {
      setError('Erro ao reenviar código.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <Helmet>
        <title>Login Cliente - Bloco B3</title>
      </Helmet>
      <Container component="main" maxWidth="xs">
        <CssBaseline/>
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Avatar sx={{ m: 1, bgcolor: 'primary.main' }}>
            <LockOutlined/>
          </Avatar>
          <Typography component="h1" variant="h5">
            Portal do Cliente
          </Typography>

          {step === 'login' ? (
            <Box component="form" onSubmit={handleRequestOtp} sx={{ mt: 3, width: '100%' }}>
              <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
                Informe seu CPF e email para receber o código de acesso.
              </Typography>

              <TextField
                margin="normal"
                required
                fullWidth
                label="CPF"
                size="small"
                value={cpf}
                onChange={(e) => setCpf(Masks.cpf(e.target.value))}
                inputProps={{ maxLength: 14 }}
                placeholder="000.000.000-00"
              />

              <TextField
                margin="normal"
                required
                fullWidth
                label="Email"
                size="small"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="seu@email.com"
              />

              <Fade in={!!error}>
                <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>
              </Fade>

              <LoadingButton
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                loading={loading}
                loadingIndicator={<CircularProgress size={22} color="inherit"/>}
                disabled={cpf.replace(/\D/g, '').length < 11 || !email}
              >
                Enviar código
              </LoadingButton>

              <Box sx={{ textAlign: 'center', mt: 1 }}>
                <Link href="/login" variant="body2" underline="hover">
                  Voltar para login principal
                </Link>
              </Box>
            </Box>
          ) : (
            <Box component="form" onSubmit={handleValidateOtp} sx={{ mt: 3, width: '100%' }}>
              <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
                Digite o código de 6 dígitos enviado para <strong>{email}</strong>
              </Typography>

              <OtpInput
                value={otpCode}
                onChange={setOtpCode}
                disabled={loading}
                error={!!error}
              />

              {timer > 0 && (
                <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2 }}>
                  Código expira em <strong>{formatTimer(timer)}</strong>
                </Typography>
              )}

              <Fade in={!!error}>
                <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
              </Fade>

              <LoadingButton
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                loading={loading}
                loadingIndicator={<CircularProgress size={22} color="inherit"/>}
                disabled={otpCode.length !== 6}
              >
                Verificar
              </LoadingButton>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                <Link
                  component="button"
                  variant="body2"
                  underline="hover"
                  onClick={() => { setStep('login'); setError(''); setOtpCode('') }}
                  type="button"
                >
                  Voltar
                </Link>
                <Link
                  component="button"
                  variant="body2"
                  underline="hover"
                  onClick={handleResendCode}
                  disabled={timer > 0 || loading}
                  type="button"
                  sx={{ color: timer > 0 ? 'text.disabled' : 'primary.main' }}
                >
                  Reenviar código
                </Link>
              </Box>
            </Box>
          )}
        </Box>
      </Container>
    </>
  )
}

export default TenantLogin
